import React, { useState } from "react";
import {
  ArrowLeft,
  Bookmark,
  Heart,
  Clock,
  ChevronRight,
} from "lucide-react";
import CustomCollection from "./collections/CustomCollection";

const CollectionsSection = ({ onBack }) => {
  const [currentView, setCurrentView] = useState("main");

  const collectionTypes = [
    {
      id: "favourites",
      icon: Heart,
      label: "Favourites",
      description: "Your favorite posts and content",
      type: "favourite",
    },
    {
      id: "read-later",
      icon: Clock,
      label: "Read Later",
      description: "Posts you saved to read later",
      type: "read-later",
    },
    {
      id: "saved",
      icon: Bookmark,
      label: "Saved Posts",
      description: "Your bookmarked posts and content",
      type: "save",
    },
  ];

  const handleBackToMain = () => {
    setCurrentView("main");
  };

  const getCurrentCollectionConfig = () => {
    const collection = collectionTypes.find((c) => c.id === currentView);
    if (!collection) return null;

    const configs = {
      favourites: {
        title: "Favourites",
        description: "Your favorite posts and content",
        emptyIcon: Heart,
        emptyTitle: "No Favourites Yet",
        emptyDescription: "Start liking posts to see them here",
      },
      "read-later": {
        title: "Read Later",
        description: "Posts you saved to read later",
        emptyIcon: Clock,
        emptyTitle: "Nothing to Read Later",
        emptyDescription: "Save posts to read later to see them here",
      },
      saved: {
        title: "Saved Posts",
        description: "Your bookmarked posts and content",
        emptyIcon: Bookmark,
        emptyTitle: "No Saved Posts",
        emptyDescription: "Start bookmarking posts to see them here",
      },
    };

    return {
      collectionType: collection.type,
      ...configs[currentView],
    };
  };

  if (currentView !== "main") {
    const collectionConfig = getCurrentCollectionConfig();

    if (!collectionConfig) {
      return (
        <div className="flex-1 min-w-0">
          <div className="text-center py-12">
            <p className="text-red-500">Collection not found</p>
            <button
              onClick={handleBackToMain}
              className="mt-4 px-4 py-2 bg-black text-white rounded"
            >
              Back to Collections
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 min-w-0">
        <CustomCollection
          onBack={handleBackToMain}
          collectionType={collectionConfig.collectionType}
          title={collectionConfig.title}
          description={collectionConfig.description}
          emptyIcon={collectionConfig.emptyIcon}
          emptyTitle={collectionConfig.emptyTitle}
          emptyDescription={collectionConfig.emptyDescription}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 sm:px-0">
      {/* Collection Types */}
      <div className="bg-white py-2">
        <h3 className="font-semibold text-black mb-4">Collections</h3>

        <div className="space-y-0">
          {collectionTypes.map((collection, index) => (
            <button
              key={collection.id}
              onClick={() => setCurrentView(collection.id)}
              className={`flex items-center justify-between w-full py-4 px-2 hover:bg-gray-100 transition-colors ${
                index !== collectionTypes.length - 1 ? "border-b border-black" : ""
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 text-gray-600">
                  <collection.icon className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-black">
                    {collection.label}
                  </p>
                  <p className="text-xs text-gray-600">
                    {collection.description}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Information Section */}
      <div className="bg-white rounded-lg p-3 border border-black">
        <p className="text-sm text-gray-600 leading-relaxed">
          <strong className="text-black">About collections.</strong> Keep your favorite content organized and
          easily accessible by saving posts to Favorites, Read Later, or Saved Posts.
        </p>

        <p className="text-xs text-gray-600 mt-2">
          Collections help you build a personal library of posts so you can revisit
          interesting content anytime without losing track of it.
        </p>
      </div>
    </div>
  );
};

export default CollectionsSection;